// Sprint 2026-Feb-11i-hotfix — Generic React ErrorBoundary.
//
// Purpose: prevent a single component render exception from unmounting
// the entire app (blank white page). Any child that throws during
// render / lifecycle now shows a small red inline banner and offers a
// "Reload page" fallback. All other tabs / navigation remain intact.
//
// Wrap any page that touches user input + async API calls, e.g.:
//   <ErrorBoundary label="Email Configuration">
//     <EmailConfigurationPage />
//   </ErrorBoundary>
//
// The boundary logs the error (`console.error`) so it still shows in
// devtools + browser log stream — we do NOT swallow the trace.

import React from "react";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error(
      `[ErrorBoundary${this.props.label ? `:${this.props.label}` : ""}]`,
      error,
      info?.componentStack,
    );
  }

  handleReload = () => {
    this.setState({ error: null });
    // Soft reset — parent may re-mount children. A full reload is a
    // fallback in case a stale prop caused the crash.
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg =
      (this.state.error && (this.state.error.message || String(this.state.error))) ||
      "Something went wrong while rendering this page.";
    return (
      <div
        className="m-4 border border-rose-300 bg-rose-50 text-rose-900 p-4 rounded"
        data-testid="error-boundary-fallback"
        role="alert"
      >
        <div className="text-sm font-bold mb-1">
          {this.props.label ? `${this.props.label} — ` : ""}Something went wrong
        </div>
        <div className="text-xs text-rose-800 mb-3 break-words">
          {msg}
        </div>
        <button
          type="button"
          onClick={this.handleReload}
          className="text-xs bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded"
          data-testid="error-boundary-reload"
        >
          Reload page
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
